import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, BookOpen, Calculator, FlaskConical, Loader2, CreditCard, Sparkles, GraduationCap, Globe, Zap, Award } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { id: "standard", label: "Standard (KS1–KS3)", desc: "Years 1–9", icon: BookOpen },
  { id: "gcse", label: "GCSE (KS4)", desc: "Years 10–11", icon: GraduationCap },
  { id: "eleven_plus", label: "11+ Preparation", desc: "Grammar & selective school prep", icon: Award },
  { id: "esol", label: "ESOL", desc: "English for Speakers of Other Languages", icon: Globe },
  { id: "independent", label: "Independent Study", desc: "App-only, self-paced access", icon: Zap },
];

const SUBJECT_PRICING = {
  standard: { 1: { id: "standard_1", price: 89, name: "1 Subject" }, 2: { id: "standard_2", price: 159, name: "2 Subjects", popular: true }, 3: { id: "standard_3", price: 179, name: "3 Subjects" } },
  gcse: { 1: { id: "gcse_1", price: 99, name: "GCSE 1 Subject" }, 2: { id: "gcse_2", price: 179, name: "GCSE 2 Subjects" }, 3: { id: "gcse_3", price: 199, name: "GCSE 3 Subjects" } },
};

const FIXED_PACKAGES = {
  eleven_plus: { id: "eleven_plus", price: 119, name: "11+ Preparation" },
  esol: { id: "esol", price: 69, name: "ESOL" },
  independent: { id: "independent", price: 49, name: "Independent Study (App Only)" },
};

const subjectIcons = { maths: Calculator, english: BookOpen, science: FlaskConical };

const ParentReportPage = () => {
  const { reportToken } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [childName, setChildName] = useState('');
  const [yearGroup, setYearGroup] = useState(null);
  const [choicesSubmitted, setChoicesSubmitted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const paymentCancelled = searchParams.get('payment') === 'cancelled';

  useEffect(() => { fetchReport(); }, [reportToken]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/assessment/parent-report/${reportToken}`);
      setReport(res.data.report);
      setChildName(res.data.child_name);
      setYearGroup(res.data.child_year_group);
      setChoicesSubmitted(res.data.choices_submitted);
      setPaymentStatus(res.data.payment_status);
      if (res.data.parent_subject_choices) setSelectedSubjects(res.data.parent_subject_choices);
    } catch (err) {
      if (err.response?.status === 404) setError('This report link is invalid or has expired.');
      else if (err.response?.status === 400) setError(err.response.data.detail || 'This assessment has already been processed.');
      else setError('Failed to load the report. Please try again later.');
    } finally { setLoading(false); }
  };

  const needsSubjects = selectedTier === 'standard' || selectedTier === 'gcse';

  const toggleSubject = (subject) => {
    if (paymentStatus === 'paid') return;
    setSelectedSubjects(prev =>
      prev.includes(subject) ? prev.filter(s => s !== subject) : [...prev, subject]
    );
  };

  const getResolvedPackage = () => {
    if (!selectedTier) return null;
    if (needsSubjects) {
      const count = selectedSubjects.length;
      if (count === 0) return null;
      return SUBJECT_PRICING[selectedTier]?.[count] || null;
    }
    return FIXED_PACKAGES[selectedTier] || null;
  };

  const handleProceedToPayment = async () => {
    const pkg = getResolvedPackage();
    if (!pkg) { alert('Please complete your selection.'); return; }

    try {
      setSubmitting(true);
      const subjects = needsSubjects ? selectedSubjects : [];
      const res = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        report_token: reportToken,
        subjects,
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create payment session. Please try again.');
      setSubmitting(false);
    }
  };

  const getScoreColor = (p) => p >= 70 ? 'text-green-600' : p >= 50 ? 'text-amber-600' : 'text-red-500';
  const getScoreBg = (p) => p >= 70 ? 'bg-green-50 border-green-200' : p >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  const suggestedTier = yearGroup >= 10 ? 'gcse' : 'standard';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Report</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const resolvedPkg = getResolvedPackage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div data-testid="report-header" className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-2xl p-8 text-white text-center">
          <h1 className="text-3xl font-bold mb-2">{childName}'s Assessment Results</h1>
          <p className="text-emerald-100">Year {yearGroup}</p>
        </div>

        <div className="bg-white rounded-b-2xl shadow-xl p-6 md:p-8">
          {/* Overall Score */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-8 text-center border border-emerald-200">
            <p className="text-sm text-emerald-600 uppercase tracking-wide font-medium">Overall Score</p>
            <p data-testid="overall-score" className={`text-6xl font-bold mt-2 ${getScoreColor(report?.overall_percentage || 0)}`}>
              {report?.overall_percentage || 0}%
            </p>
            <p className="text-gray-600 mt-2">
              Recommended Starting Level: <span className="font-semibold">Year {report?.overall_recommended_year || 'N/A'}</span>
            </p>
          </div>

          {/* Subject Breakdown */}
          <h2 className="text-xl font-bold text-gray-800 mb-4">Subject Results</h2>
          <div className="grid gap-4 mb-8">
            {['maths', 'english', 'science'].map(subject => {
              const d = report?.subjects?.[subject] || {};
              const pct = d.percentage || 0;
              const Icon = subjectIcons[subject];
              return (
                <div key={subject} data-testid={`subject-result-${subject}`} className={`rounded-xl p-4 border-2 ${getScoreBg(pct)}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${pct >= 70 ? 'bg-green-100' : pct >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
                        <Icon className={`w-6 h-6 ${getScoreColor(pct)}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 capitalize">{subject}</h3>
                        <p className="text-sm text-gray-500">{d.correct || 0} / {d.total || 10} correct</p>
                      </div>
                    </div>
                    <p className={`text-3xl font-bold ${getScoreColor(pct)}`}>{pct}%</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4 text-sm">
                    {d.strengths?.length > 0 && (
                      <div><p className="text-green-600 font-medium mb-1">Strengths:</p><p className="text-gray-600">{d.strengths.join(', ')}</p></div>
                    )}
                    {d.weaknesses?.length > 0 && (
                      <div><p className="text-amber-600 font-medium mb-1">Areas to develop:</p><p className="text-gray-600">{d.weaknesses.join(', ')}</p></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommendation */}
          {report?.starting_point && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8">
              <h3 className="font-semibold text-amber-800 mb-2">Our Recommendation</h3>
              <p className="text-amber-700">{report.starting_point}</p>
            </div>
          )}

          {/* ========== ENROLMENT SECTION ========== */}
          <div className="border-t pt-8 mt-8">
            {paymentStatus === 'paid' ? (
              <div data-testid="payment-complete-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                <p className="text-emerald-700 font-semibold text-lg">Payment complete!</p>
                <p className="text-emerald-600 text-sm mt-1">{childName}'s account has been created. Check your email for login details.</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Choose Your Package</h2>
                <p className="text-gray-600 mb-6">
                  Select the programme that best suits {childName}'s learning needs.
                  {yearGroup >= 10 && (
                    <span className="block text-sm text-purple-600 mt-1">
                      Based on Year {yearGroup}, we suggest the <strong>GCSE</strong> package — but you can choose any tier.
                    </span>
                  )}
                </p>

                {paymentCancelled && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-amber-700">Payment was cancelled. You can try again below.</p>
                  </div>
                )}

                {/* Tier Selection */}
                <div data-testid="tier-selection" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                  {TIER_OPTIONS.map(tier => {
                    const active = selectedTier === tier.id;
                    const suggested = tier.id === suggestedTier;
                    const TierIcon = tier.icon;
                    return (
                      <button
                        key={tier.id}
                        data-testid={`tier-${tier.id}`}
                        onClick={() => { setSelectedTier(tier.id); setSelectedSubjects([]); }}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                          active
                            ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-200'
                            : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'
                        }`}
                      >
                        {suggested && !active && (
                          <span className="absolute -top-2.5 right-3 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">Suggested</span>
                        )}
                        <div className="flex items-center gap-3 mb-1">
                          <TierIcon className={`w-5 h-5 ${active ? 'text-emerald-600' : 'text-gray-400'}`} />
                          <span className={`font-semibold text-sm ${active ? 'text-emerald-700' : 'text-gray-800'}`}>{tier.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-8">{tier.desc}</p>
                        {active && <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-emerald-500" />}
                      </button>
                    );
                  })}
                </div>

                {/* Subject Selection — only for standard / gcse */}
                {needsSubjects && selectedTier && (
                  <div data-testid="subject-selection" className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Select Subjects
                      <span className="text-sm font-normal text-gray-500 ml-2">(choose 1–3)</span>
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {['maths', 'english', 'science'].map(subject => {
                        const isSelected = selectedSubjects.includes(subject);
                        const Icon = subjectIcons[subject];
                        return (
                          <button
                            key={subject}
                            data-testid={`subject-btn-${subject}`}
                            onClick={() => toggleSubject(subject)}
                            className={`p-5 rounded-xl border-2 transition-all ${
                              isSelected
                                ? 'border-emerald-500 bg-emerald-50 shadow-md'
                                : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${isSelected ? 'bg-emerald-500' : 'bg-gray-100'}`}>
                              <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                            </div>
                            <p className={`font-semibold capitalize text-sm ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>{subject}</p>
                            {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto mt-1" />}
                          </button>
                        );
                      })}
                    </div>

                    {/* Subject-count pricing cards */}
                    {selectedTier && (
                      <div className="mt-5 grid grid-cols-3 gap-3">
                        {[1, 2, 3].map(n => {
                          const p = SUBJECT_PRICING[selectedTier][n];
                          const active = selectedSubjects.length === n;
                          return (
                            <div key={n} className={`text-center p-3 rounded-lg border ${active ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 bg-gray-50'}`}>
                              <p className="text-xs text-gray-500 uppercase">{n} Subject{n > 1 ? 's' : ''}</p>
                              <p className={`text-xl font-bold ${active ? 'text-emerald-700' : 'text-gray-700'}`}>&pound;{p.price}</p>
                              <p className="text-xs text-gray-400">/month</p>
                              {p.popular && <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Most Popular</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Fixed-price package summary for special tiers */}
                {!needsSubjects && selectedTier && FIXED_PACKAGES[selectedTier] && (
                  <div data-testid="fixed-package-summary" className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5 mb-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-800 font-semibold text-lg">{FIXED_PACKAGES[selectedTier].name}</p>
                        <p className="text-purple-600 text-sm mt-1">
                          {selectedTier === 'eleven_plus' && 'Verbal & non-verbal reasoning, maths & English prep'}
                          {selectedTier === 'esol' && 'Speaking, listening, reading & writing practice'}
                          {selectedTier === 'independent' && 'Full app access — self-paced, no tutor sessions'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-purple-700">&pound;{FIXED_PACKAGES[selectedTier].price}</p>
                        <p className="text-purple-500 text-sm">/month</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dynamic pricing summary for subject-based tiers */}
                {needsSubjects && selectedSubjects.length > 0 && resolvedPkg && (
                  <div data-testid="pricing-summary" className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-purple-800 font-semibold text-lg">
                          {selectedTier === 'gcse' ? 'GCSE' : 'Standard'} — {resolvedPkg.name}
                        </p>
                        <p className="text-purple-600 text-sm">
                          {selectedSubjects.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-purple-700">&pound;{resolvedPkg.price}</p>
                        <p className="text-purple-500 text-sm">/month</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message to Growing Minds (Optional)
                  </label>
                  <textarea
                    data-testid="parent-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Any questions or specific requirements..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    rows={3}
                  />
                </div>

                {/* Payment Button */}
                <button
                  data-testid="proceed-to-payment-btn"
                  onClick={handleProceedToPayment}
                  disabled={submitting || !resolvedPkg}
                  className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all ${
                    resolvedPkg
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-lg'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to payment...</>
                  ) : (
                    <><CreditCard className="w-5 h-5" /> Proceed to Payment{resolvedPkg ? ` — \u00A3${resolvedPkg.price}` : ''}</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>Growing Minds Tuition</p>
          <p>Expert tutoring for every stage of learning</p>
        </div>
      </div>
    </div>
  );
};

export default ParentReportPage;
